import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";

interface Endpoint {
  id: string;
  method: string;
  path: string;
  group: string;
  action: string;
}
interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  expiresAt: string;
  revokedAt: string | null;
  createdAt: string;
  permissions: string[];
  rateLimitPermitLimit: number;
  rateLimitWindowSeconds: number;
}

const ApiKeysPage = () => {
  const { token } = useAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [selected, setSelected] = useState<Endpoint[]>([]);
  const [name, setName] = useState("");
  const [permits, setPermits] = useState(60);
  const [windowSeconds, setWindowSeconds] = useState(60);
  const [editing, setEditing] = useState<string | null>(null);
  const [expires, setExpires] = useState(() =>
    new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
  );
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [revoke, setRevoke] = useState<ApiKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };
  const docsUrl = `${(apiVariable || "").replace(/\/api\/?$/, "")}/swagger/index.html`;

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [list, permissions] = await Promise.all([
        axios.get<ApiKey[]>(`${apiVariable}/api-keys`, config),
        axios.get<Endpoint[]>(`${apiVariable}/api-keys/endpoints`, config),
      ]);
      setKeys(list.data);
      setEndpoints(permissions.data);
    } catch {
      setError(
        "Could not load API keys. Check your access and that the API key database update has been applied.",
      );
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setError("");
    setCopied(false);
    try {
      const request = {
        name: name.trim(),
        expiresAt: new Date(`${expires}T23:59:59`).toISOString(),
        permissions: selected.map((e) => e.id),
        rateLimitPermitLimit: permits,
        rateLimitWindowSeconds: windowSeconds,
      };
      if (editing) {
        const { data } = await axios.put<ApiKey>(
          `${apiVariable}/api-keys/${editing}`,
          request,
          { headers },
        );
        setKeys((previous) =>
          previous.map((key) => (key.id === editing ? data : key)),
        );
      } else {
        const { data } = await axios.post<{ key: string; apiKey: ApiKey }>(
          `${apiVariable}/api-keys`,
          request,
          { headers },
        );
        setSecret(data.key);
        setKeys((previous) => [data.apiKey, ...previous]);
      }
      setName("");
      setSelected([]);
      setEditing(null);
    } catch (e) {
      setError(
        axios.isAxiosError(e) && typeof e.response?.data === "string"
          ? e.response.data
          : "Could not save API key. Check the name, permissions, expiry, and rate limit.",
      );
    } finally {
      setBusy(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revoke) return;
    setBusy(true);
    setError("");
    try {
      await axios.delete(`${apiVariable}/api-keys/${revoke.id}`, { headers });
      setRevoke(null);
      await load();
    } catch {
      setError("Could not revoke this key. Please retry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4">API integrations</Typography>
        <Button href={docsUrl} target="_blank" rel="noopener noreferrer">
          Endpoint documentation
        </Button>
      </Stack>
      <Typography mb={2}>
        Select the existing GET endpoints each integration can access, then set its
        expiry and request limit. HR employee data is excluded.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      <Stack spacing={2} sx={{ mb: 4 }}>
        {editing && (
          <Alert severity="info">
            Editing an existing integration. Its key stays the same.
          </Alert>
        )}
        <TextField
          label="Integration name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 120 }}
        />
        <Stack direction="row" spacing={2}>
          <TextField
            type="number"
            label="Requests per window"
            value={permits}
            onChange={(e) => setPermits(Number(e.target.value))}
            inputProps={{ min: 1, max: 10000, step: 1 }}
            sx={{ width: 150 }}
          />
          <TextField
            type="number"
            label="Window (seconds)"
            value={windowSeconds}
            onChange={(e) => setWindowSeconds(Number(e.target.value))}
            inputProps={{ min: 1, max: 86400, step: 1 }}
            sx={{ width: 150 }}
          />
        </Stack>
        <TextField
          type="date"
          label="Expires on"
          value={expires}
          onChange={(e) => setExpires(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="Choose an expiry within the next year."
        />
        <Autocomplete
          multiple
          options={endpoints}
          value={selected}
          groupBy={(e) => e.group}
          getOptionLabel={(e) => `${e.method} ${e.path} · ${e.action}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, values) => setSelected(values)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Allowed GET endpoints"
              placeholder="Search by path or action"
            />
          )}
          renderTags={(values, getTagProps) =>
            values.map((value, index) => (
              <Chip label={value.id} {...getTagProps({ index })} size="small" />
            ))
          }
        />
        <Box>
          <Button
            variant="contained"
            disabled={
              busy ||
              loading ||
              !token ||
              !name.trim() ||
              !expires ||
              !Number.isInteger(permits) ||
              permits < 1 ||
              permits > 10000 ||
              !Number.isInteger(windowSeconds) ||
              windowSeconds < 1 ||
              windowSeconds > 86400 ||
              !selected.length
            }
            onClick={save}
          >
            {busy ? "Saving…" : editing ? "Save integration" : "Create API key"}
          </Button>
          {editing && (
            <Button
              disabled={busy}
              onClick={() => {
                setEditing(null);
                setName("");
                setSelected([]);
              }}
            >
              Cancel edit
            </Button>
          )}
        </Box>
      </Stack>
      <Typography variant="h6">Issued keys</Typography>
      {loading ? (
        <CircularProgress aria-label="Loading API keys" />
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                {[
                  "Integration",
                  "Key prefix",
                  "Access",
                  "Rate limit",
                  "Expires",
                  "Status",
                  "",
                ].map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell>{key.name}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {key.prefix}…
                  </TableCell>
                  <TableCell>
                    <details>
                      <summary>{key.permissions.length} endpoints</summary>
                      {key.permissions.map((p) => (
                        <Typography
                          key={p}
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {p}
                        </Typography>
                      ))}
                    </details>
                  </TableCell>
                  <TableCell>
                    {key.rateLimitPermitLimit} / {key.rateLimitWindowSeconds}s
                  </TableCell>
                  <TableCell>
                    {new Date(key.expiresAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {key.revokedAt
                      ? "Revoked"
                      : new Date(key.expiresAt) <= new Date()
                        ? "Expired"
                        : key.permissions.some(
                              (permission) =>
                                !endpoints.some(
                                  (endpoint) => endpoint.id === permission,
                                ),
                            )
                          ? "Needs access review"
                          : "Active"}
                  </TableCell>
                  <TableCell>
                    <Button
                      disabled={busy || !!key.revokedAt}
                      onClick={() => {
                        setEditing(key.id);
                        setName(key.name);
                        setExpires(key.expiresAt.slice(0, 10));
                        setSelected(
                          endpoints.filter((endpoint) =>
                            key.permissions.includes(endpoint.id),
                          ),
                        );
                        setPermits(key.rateLimitPermitLimit);
                        setWindowSeconds(key.rateLimitWindowSeconds);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      Edit access
                    </Button>
                    <Button
                      color="error"
                      disabled={busy || !!key.revokedAt}
                      onClick={() => setRevoke(key)}
                    >
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!keys.length && (
                <TableRow>
                  <TableCell colSpan={7}>No API keys created yet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}
      <Dialog open={secret !== null} maxWidth="md" fullWidth>
        <DialogTitle>Copy your API key</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the only time the full key will be shown. Save it securely
            before closing.
          </Alert>
          <TextField
            fullWidth
            multiline
            value={secret || ""}
            InputProps={{ readOnly: true }}
            label="API key"
          />
          <Typography mt={2}>
            Send it in the <code>X-API-Key</code> header over HTTPS.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(secret || "");
                setCopied(true);
              } catch {
                setError(
                  "Clipboard access failed. Select and copy the key from the field.",
                );
              }
            }}
          >
            {copied ? "Copied" : "Copy key"}
          </Button>
          <Button onClick={() => setSecret(null)}>I have saved the key</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!revoke}
        onClose={() => {
          if (!busy) setRevoke(null);
        }}
      >
        <DialogTitle>Revoke {revoke?.name}?</DialogTitle>
        <DialogContent>
          This integration will lose access immediately. Create a new key to
          restore its access.
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setRevoke(null)}>
            Cancel
          </Button>
          <Button color="error" disabled={busy} onClick={confirmRevoke}>
            Revoke key
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiKeysPage;
